// 111 §5c — THE LINK CLUSTER HIGHLIGHT.
//
// Rows of all three kinds carry `data-lnk="<kind>-<id>"`; a click on a row's
// Link icon highlights the item AND every linked item the current view
// renders (`.lnkHl`; colour from the `--hll` token, §5d).
//
// 113 §1 — THE HIGHLIGHT IS THE AMBER'S TWIN NOW, so "steady, no flash" is
// gone from this sentence: `.lnkHl` runs `lnkHold`, which is `evHold` in the
// link token, and the window below is also the animation's DURATION — passed
// to CSS as `--lnk-hl-ms` so LINK_HL_BASE_MS stays the one place the number
// is written. The class comes off an element that already faded to nothing,
// which is why the removal cannot be seen.
// Cross-view reach is deliberately NOT attempted: the resolver queries the
// live DOM, so what is not rendered is not highlighted (the brief's rule).
//
// TIMED, WITH THE SCROLL ASSIST (RULED 2026-08-21):
//   (a) if any member sits outside the viewport, the TOPMOST highlighted
//       element scrolls into view (smooth, block start) so the cluster reads
//       top-down;
//   (b) the highlight lasts LINK_HL_BASE_MS — DOUBLED when the click caused
//       a scroll;
//   (c) a re-click re-arms the window, a click on another link icon replaces
//       the cluster (one cluster at a time), and the amber jump (.evHl)
//       coexists untouched — different class, different token.
// The Eisenhower surfaces pass { scroll: false } and highlight in place (v-f).

export type LinkRef = { k: "stage" | "task" | "event"; id: number };
export type ItemLinkRow = { id: number; a: LinkRef; b: LinkRef };

/** §5c-b's named base duration. */
export const LINK_HL_BASE_MS = 6000;

const sameRef = (x: LinkRef, y: LinkRef) => x.k === y.k && Number(x.id) === Number(y.id);

/** The links touching `item` on a matter row (itemLinks array). */
export const linksOfItem = (matterRow: { itemLinks?: unknown } | null | undefined, item: LinkRef): ItemLinkRow[] => {
  const arr = Array.isArray(matterRow?.itemLinks) ? (matterRow!.itemLinks as ItemLinkRow[]) : [];
  return arr.filter((l) => l && l.a && l.b && (sameRef(l.a, item) || sameRef(l.b, item)));
};

/* 112 §3 — THE CLUSTER IS THE WHOLE CHAIN, NOT THE NEIGHBOURS (RULED).
   `linkedRefsOf` (the direct partners of the clicked item) stood here and made
   what lights up depend on WHERE you click: T at the end of S–E–T lit only E.
   Laurent's rule: clicking any member highlights the entire connected
   component — every item reachable through any path of links, regardless of
   direction or where the chain was built. So the neighbour lookup is REPLACED
   by this closure walk (BFS over the matter row's itemLinks; the arrays are
   small — chains produce a handful of links — so no memo, no index).
   EXCLUDES `item` itself, deliberately: the caller (LinkIcon) treats "no
   reachable others" as "unlinked, render nothing", and an unlinked item's
   component is just itself — so presence semantics are unchanged while the
   highlighted set widens. The scroll assist and the doubled window key off
   the same set by construction (highlightLinkCluster receives it whole).
   Unlink-all semantics are untouched — it still removes only the named item's
   own links; the component simply re-computes at the next click. */
export const connectedComponentOf = (
  matterRow: { itemLinks?: unknown } | null | undefined,
  item: LinkRef,
): LinkRef[] => {
  const arr = Array.isArray(matterRow?.itemLinks) ? (matterRow!.itemLinks as ItemLinkRow[]) : [];
  const key = (r: LinkRef) => `${r.k}-${Number(r.id)}`;
  const seen = new Set<string>([key(item)]);
  const out: LinkRef[] = [];
  const queue: LinkRef[] = [item];
  while (queue.length) {
    const cur = queue.shift()!;
    for (const l of arr) {
      if (!l || !l.a || !l.b) continue;
      const other = sameRef(l.a, cur) ? l.b : sameRef(l.b, cur) ? l.a : null;
      if (!other) continue;
      const k = key(other);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(other);
      queue.push(other);
    }
  }
  return out;
};

let _active: { els: HTMLElement[]; timer: ReturnType<typeof setTimeout> } | null = null;
const _clear = () => {
  if (!_active) return;
  clearTimeout(_active.timer);
  for (const el of _active.els) {
    el.classList.remove("lnkHl");
    el.style.removeProperty("--lnk-hl-ms");
  }
  _active = null;
};

export function highlightLinkCluster(
  self: LinkRef,
  others: LinkRef[],
  opts?: { scroll?: boolean },
) {
  _clear(); // re-click re-arms; another icon replaces
  const refs = [self, ...others];
  const els: HTMLElement[] = [];
  for (const r of refs) {
    document
      .querySelectorAll<HTMLElement>(`[data-lnk="${r.k}-${Number(r.id)}"]`)
      .forEach((el) => els.push(el));
  }
  if (!els.length) return;
  /* 113 §1 — THE SCROLL DECISION MOVES AHEAD OF THE CLASS, and it has to.
     `.lnkHl` is an ANIMATION now (`lnkHold`, the amber's own anatomy) whose
     duration IS this window, so the doubled-on-scroll figure must be known
     before the class goes on: an `animation-duration` changed mid-flight
     re-times a running animation rather than restarting it, which reads as a
     jump. Nothing in the measurement below depends on the class — the rects
     are the same either way — so the reorder is free. */
  let scrolled = false;
  if (opts?.scroll !== false) {
    const outside = els.some((el) => {
      const b = el.getBoundingClientRect();
      return b.bottom < 0 || b.top > window.innerHeight;
    });
    if (outside) {
      const top = els.reduce((m, el) =>
        el.getBoundingClientRect().top < m.getBoundingClientRect().top ? el : m,
      );
      top.scrollIntoView({ behavior: "smooth", block: "start" });
      scrolled = true;
    }
  }
  const ms = LINK_HL_BASE_MS * (scrolled ? 2 : 1);
  for (const el of els) {
    /* A class removed and re-added inside ONE task is no change at all to the
       style system — it is recomputed once, at the end — so a re-click on an
       element still holding `.lnkHl` would leave the old animation running
       from where it was, and §5c's "a re-click re-arms the window" would be
       true of the timer and false of the fade. The reflow read between the
       two is what makes the restart real. It costs one layout on a handful of
       rows, and only on a click. */
    el.classList.remove("lnkHl");
    void el.offsetWidth;
    el.style.setProperty("--lnk-hl-ms", `${ms}ms`);
    el.classList.add("lnkHl");
  }
  _active = { els, timer: setTimeout(_clear, ms) };
}
